'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { toast } from 'sonner';
import { clsx } from 'clsx';
import { Box } from 'lucide-react';
import {
  Modal,
  Spinner,
  accentBorderStyle,
  accentIconStyle,
} from '@paeffgen/react/crm-documents/ui';
import {
  MATERIAL_TEMPLATES_QUERY,
  CREATE_MATERIAL_TEMPLATE_MUTATION,
  UPDATE_MATERIAL_TEMPLATE_MUTATION,
  type MaterialTemplate,
  type MaterialTemplateItem,
  type MaterialTemplateItemInput,
} from '@paeffgen/react/crm-documents/data-access-graphql';

export interface SaveToProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Die zu speichernde Auswahl — inkl. Mengen, vom Aufrufer gebaut. */
  items: MaterialTemplateItemInput[];
  selectedCount: number;
  /** Scope für die Projektliste (= adminQueryVars). */
  queryVars: Record<string, unknown>;
  /** Scope für Create/Update (= adminMutationVars bzw. { customerNumber }). */
  mutationVars: { customerNumber?: string };
  /** Nach erfolgreichem Speichern (neu ODER bestehend). Aufrufer macht hier
   *  z. B. markHighlight('projekte') + Auswahl leeren. */
  onSaved: () => void;
  initialName?: string;
}

/**
 * Führt die vorhandenen Positionen eines Projekts mit der neuen Auswahl zusammen.
 * Da das UPDATE serverseitig die komplette Liste ersetzt, schicken wir
 * „vorhandene + neue". Bereits enthaltene Artikelnummern werden **übersprungen**
 * (Menge/Notiz der vorhandenen Position bleibt unverändert); nur wirklich neue
 * Artikelnummern werden ergänzt. Anzeigefelder (description/unit …) sind nicht
 * Teil des Inputs und werden bewusst weggelassen.
 */
export function mergeItems(
  existing: MaterialTemplateItem[],
  incoming: MaterialTemplateItemInput[],
): MaterialTemplateItemInput[] {
  const base: MaterialTemplateItemInput[] = existing.map((it) => ({
    articleNumber: it.articleNumber,
    quantity: it.quantity,
    ...(it.note != null ? { note: it.note } : {}),
  }));
  const present = new Set(base.map((it) => it.articleNumber));
  for (const inc of incoming) {
    if (!present.has(inc.articleNumber)) {
      base.push(inc);
      present.add(inc.articleNumber);
    }
  }
  return base;
}

/**
 * „Als Projekt speichern" — die Auswahl entweder in ein NEUES Projekt speichern
 * oder einem BESTEHENDEN Projekt hinzufügen. Selbstversorgend: lädt die
 * Projektliste und kapselt Create-/Update-Mutation (eine Stelle für alle
 * Aufrufer: Favoriten, Artikelsuche Desktop + Mobile).
 */
export function SaveToProjectModal({
  isOpen,
  onClose,
  items,
  selectedCount,
  queryVars,
  mutationVars,
  onSaved,
  initialName = '',
}: SaveToProjectModalProps) {
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [templateName, setTemplateName] = useState(initialName);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isOpen) {
      setMode('new');
      setTemplateName(initialName);
      setSelectedTemplateId(null);
      setSearch('');
    }
  }, [isOpen, initialName]);

  const { data, loading } = useQuery<{ materialTemplates: MaterialTemplate[] }>(
    MATERIAL_TEMPLATES_QUERY,
    { variables: queryVars, skip: !isOpen, fetchPolicy: 'cache-and-network' },
  );
  const templates = useMemo(() => data?.materialTemplates ?? [], [data]);

  const refetch = [
    { query: MATERIAL_TEMPLATES_QUERY, variables: queryVars },
  ];
  const [createTemplate, { loading: creating }] = useMutation(
    CREATE_MATERIAL_TEMPLATE_MUTATION,
    { refetchQueries: refetch },
  );
  const [updateTemplate, { loading: updating }] = useMutation(
    UPDATE_MATERIAL_TEMPLATE_MUTATION,
    { refetchQueries: refetch },
  );
  const busy = creating || updating;

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q
      ? templates.filter((t) => t.name.toLowerCase().includes(q))
      : templates;
  }, [templates, search]);

  const handleCreate = async () => {
    const name = templateName.trim();
    if (!name || items.length === 0) return;
    try {
      await createTemplate({
        variables: { name, items, ...mutationVars },
      });
      toast.success(`Projekt „${name}" gespeichert`);
      onSaved();
      onClose();
    } catch {
      toast.error(`Projekt „${name}" konnte nicht gespeichert werden`);
    }
  };

  const handleAddToExisting = async () => {
    const target = templates.find((t) => t.id === selectedTemplateId);
    if (!target || items.length === 0) return;
    const merged = mergeItems(target.items, items);
    const added = merged.length - target.items.length;
    try {
      await updateTemplate({
        variables: { id: target.id, items: merged, ...mutationVars },
      });
      toast.success(
        added > 0
          ? `${added} ${added === 1 ? 'Artikel' : 'Artikel'} zu „${target.name}" hinzugefügt`
          : `Alle Artikel waren bereits in „${target.name}"`,
      );
      onSaved();
      onClose();
    } catch {
      toast.error(
        `Artikel konnten nicht zu „${target.name}" hinzugefügt werden`,
      );
    }
  };

  const tabClass = (active: boolean) =>
    clsx(
      'flex-1 h-9 rounded-lg text-sm font-semibold transition-colors',
      active
        ? 'bg-primary text-white'
        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50',
    );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Als Projekt speichern"
      className="max-w-md"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          {selectedCount} {selectedCount === 1 ? 'Artikel' : 'Artikel'} speichern.
        </p>

        {/* Modus-Umschalter */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMode('new')}
            className={tabClass(mode === 'new')}
          >
            Neues Projekt
          </button>
          <button
            type="button"
            onClick={() => setMode('existing')}
            className={tabClass(mode === 'existing')}
          >
            Bestehendes Projekt
          </button>
        </div>

        {mode === 'new' ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name des Projekts
            </label>
            <input
              autoFocus
              type="text"
              maxLength={60}
              placeholder="z.B. Standardpaket"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        ) : (
          <div>
            {loading && templates.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Spinner />
              </div>
            ) : templates.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">
                Noch keine Projekte vorhanden. Legen Sie über{' '}
                <button
                  type="button"
                  onClick={() => setMode('new')}
                  className="font-semibold text-primary hover:underline"
                >
                  „Neues Projekt"
                </button>{' '}
                eines an.
              </p>
            ) : (
              <>
                {templates.length > 8 && (
                  <input
                    type="text"
                    placeholder="Projekt suchen …"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full mb-2 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                )}
                <div className="max-h-64 overflow-y-auto -mx-1 px-1 space-y-1">
                  {filteredTemplates.map((t) => {
                    const active = t.id === selectedTemplateId;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setSelectedTemplateId(t.id)}
                        style={accentBorderStyle(t.accentColor)}
                        className={clsx(
                          'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border text-left transition-colors',
                          active
                            ? 'border-primary bg-orange-50 ring-1 ring-primary'
                            : 'border-gray-200 bg-white hover:bg-gray-50',
                        )}
                      >
                        <span
                          className="w-7 h-7 inline-flex items-center justify-center rounded-lg shrink-0"
                          style={accentIconStyle(t.accentColor)}
                        >
                          <Box size={15} />
                        </span>
                        <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 truncate">
                          {t.name}
                        </span>
                        <span className="text-xs text-gray-500 tabular-nums shrink-0">
                          {t.itemCount}{' '}
                          {t.itemCount === 1 ? 'Artikel' : 'Artikel'}
                        </span>
                      </button>
                    );
                  })}
                  {filteredTemplates.length === 0 && (
                    <p className="text-sm text-gray-500 py-4 text-center">
                      Kein Projekt gefunden.
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <div className="flex gap-3 justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Abbrechen
          </button>
          {mode === 'new' ? (
            <button
              type="button"
              onClick={handleCreate}
              disabled={!templateName.trim() || busy}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Projekt speichern
            </button>
          ) : (
            <button
              type="button"
              onClick={handleAddToExisting}
              disabled={!selectedTemplateId || busy}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Zu Projekt hinzufügen
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
